import { supabase } from './supabase'

// Shared MFA helpers between enroll and challenge components.
//
// AAL (Authenticator Assurance Level):
//   aal1 = single-factor (magic link / password)
//   aal2 = multi-factor verified
// Supabase's getAuthenticatorAssuranceLevel returns { currentLevel, nextLevel }, where nextLevel is
// the HIGHEST level the account could currently reach.
//
// WHY THIS IS NOT JUST AN AAL COMPARISON. The previous implementation returned ok whenever
// `currentLevel === nextLevel`, which reads as "nothing further is required". It is correct for the
// challenge, and silently wrong for enrolment: with NO factor registered there is nothing to step up
// TO, so nextLevel stays aal1 and the comparison is satisfied. The `enroll_required` branch was
// therefore unreachable for exactly the population it exists to catch.
//
// Measured on 2026-09-22, against the real API rather than from the documentation — a synthetic
// account driven through every stage:
//
//   no factor at all         currentLevel=aal1  nextLevel=aal1   -> old code: ok  (should be: enrol)
//   enrolled, NOT verified   currentLevel=aal1  nextLevel=aal1   -> old code: ok  (should be: enrol)
//   verified, session upgraded  currentLevel=aal2  nextLevel=aal2 -> ok            (correct)
//   fresh login, factor unused  currentLevel=aal1  nextLevel=aal2 -> challenge     (correct)
//
// That is why production shows 0 verified factors across every account: nobody declined, nobody was
// ever asked. Note the second row — an abandoned half-enrolment also sails through, so "has a
// factor row" is not the test; "has a VERIFIED factor" is.
//
// WHETHER TO REQUIRE ENROLMENT IS A POLICY DECISION, NOT A DERIVABLE FACT. It cannot be read off
// AAL, so it is passed in. It defaults to OFF: fixing the detection must not, by itself, lock every
// member of staff out at their next login — and staff here sign in by magic link, so a forced
// enrolment wall is a real operational event that the firm should choose the moment for.

export type MfaStatus =
  | { kind: 'ok' } // already aal2, or nothing is required of this account
  | { kind: 'enroll_required' } // no VERIFIED factor and policy requires one
  | { kind: 'challenge_required'; factorId: string }

export type MfaPolicy = {
  /** Require an account with no verified factor to enrol before proceeding. Default false. */
  enrollRequired: boolean
}

/**
 * PURE decision. Split out from the network calls so the rule is testable without a live session —
 * the four rows in the table above are pinned as tests.
 */
export function decideMfaStatus(input: {
  currentLevel: string | null
  nextLevel: string | null
  verifiedFactorId: string | null
  policy: MfaPolicy
}): MfaStatus {
  // A verified factor that has not been used in THIS session must be completed, whatever the policy
  // says. Someone who has set up MFA is never quietly downgraded to single-factor.
  if (input.verifiedFactorId && input.currentLevel !== input.nextLevel) {
    return { kind: 'challenge_required', factorId: input.verifiedFactorId }
  }
  // No verified factor. AAL cannot express this (proven above), so the policy is the only input.
  if (!input.verifiedFactorId && input.policy.enrollRequired) {
    return { kind: 'enroll_required' }
  }
  return { kind: 'ok' }
}

/**
 * Enrolment policy from the build environment. Config-only to flip, matching how the other dormant
 * features here are armed — no code change, no migration. Anything other than the literal 'true' is
 * off, so a typo fails safe (open) rather than locking everyone out.
 */
export function mfaPolicyFromEnv(
  env: Record<string, string | undefined> = import.meta.env as unknown as Record<
    string,
    string | undefined
  >,
): MfaPolicy {
  return { enrollRequired: env.VITE_PUBLIC_MFA_ENROLL_REQUIRED === 'true' }
}

export async function resolveMfaStatus(policy: MfaPolicy = mfaPolicyFromEnv()): Promise<MfaStatus> {
  const { data: aal, error: aalErr } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()
  if (aalErr) throw aalErr

  // The factor list is needed in every branch now — a verified factor decides between challenge and
  // ok, and its absence decides between enrol and ok. The old code could skip this call because it
  // short-circuited on AAL, which is precisely what hid the bug.
  const { data: factors, error: factorsErr } = await supabase.auth.mfa.listFactors()
  if (factorsErr) throw factorsErr

  const verifiedTotp = factors.totp.find((f) => f.status === 'verified')
  return decideMfaStatus({
    currentLevel: aal.currentLevel,
    nextLevel: aal.nextLevel,
    verifiedFactorId: verifiedTotp?.id ?? null,
    policy,
  })
}
